import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronDown, CreditCard, Loader2, LogOut, MapPin, MessageSquare, Navigation, Plus, Repeat, UserCog,
} from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money, openCheckout } from "@/lib/api";
import { useVehiclePrices } from "@/lib/useVehiclePrices";
import { useAuth } from "@/context/AuthContext";
import { AddressInput } from "@/components/AddressInput";
import { RouteMap } from "@/components/RouteMap";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";
import { VehicleGallery } from "@/components/VehicleGallery";
import { StageBadge } from "@/components/StageBadge";
import { ChatPanel } from "@/components/ChatPanel";
import { SandboxNotice } from "@/components/SandboxNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusPill } from "@/pages/Manifests";
import { DeleteLoginButton } from "@/components/DeleteLoginButton";
import { HelpButton } from "@/components/HelpButton";
import { ContactCard } from "@/components/ContactCard";
import { Assistant } from "@/components/Assistant";

const emptyPlace = { address: "", formatted_address: "", place_id: null, lat: null, lng: null };

/** What the passenger owes right now — the reservation amount first, then the balance. */
const dueNow = (t) => {
  const deposit = Math.max(0, Number(t.deposit_due || 0) - Number(t.amount_paid || 0));
  return deposit > 0 ? deposit : Number(t.balance_due || 0);
};

export default function ClientPortal() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ trips: [], invoices: [] });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    pickup: emptyPlace, dropoff: emptyPlace, trip_date: "", pickup_time: "",
    passengers: 1, vehicle_pref: "", airline: "", flight_number: "", terminal: "", notes: "",
    vehicle_id: "", pricing_mode: "mileage", hours: 3, save_card: false,
  });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stopFor, setStopFor] = useState(null);
  const [stopPlace, setStopPlace] = useState(emptyPlace);
  const [vehicles, setVehicles] = useState([]);
  const [quote, setQuote] = useState(null);
  const [miles, setMiles] = useState(null);
  const [booked, setBooked] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [chatFor, setChatFor] = useState(null);

  const cid = company?.id;

  const payTrip = async (trip) => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data: res } = await api.post(`/client/trips/${trip.id}/pay-link`);
      navigate(`/portal/client/pay/${res.pay_token}`);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!cid) return;
    api.get(`/public/company/${cid}`).then(({ data }) => setVehicles(data.vehicles || [])).catch(() => {});
  }, [cid]);

  const points = [form.pickup, form.dropoff];
  // The map stays away until both ends are known — it used to swallow the address suggestions.
  const showMap = Boolean(form.pickup?.address && form.dropoff?.address);

  // Prices appear by themselves as soon as we know where they are going — no button to find.
  const { prices, miles: pricedMiles, loading: pricing } = useVehiclePrices({
    companyId: cid, pickup: form.pickup, dropoff: form.dropoff,
    hours: form.hours, pricingMode: form.pricing_mode, email: form.customer_email,
  });

  useEffect(() => {
    const priced = prices[form.vehicle_id] || null;
    setQuote(priced);
    if (pricedMiles != null) setMiles(pricedMiles);
  }, [prices, form.vehicle_id, pricedMiles]);

  const rideAgain = (trip) => {
    setForm((f) => ({
      ...f,
      pickup: trip.pickup || emptyPlace,
      dropoff: trip.dropoff || emptyPlace,
      passengers: trip.passengers || 1,
      vehicle_id: trip.vehicle_id || "",
      vehicle_pref: trip.vehicle_name || "",
      trip_date: "", pickup_time: "",
    }));
    setQuote(null);
    setMiles(null);
    setBooked(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const payDeposit = async (bookingId) => {
    setBusy(true); setError("");
    try {
      const { data: res } = await api.post(`/public/bookings/${bookingId || booked.id}/deposit-checkout`,
        { origin_url: window.location.origin });
      if (!openCheckout(res.checkout_url, setCheckoutUrl)) setCheckoutUrl(res.checkout_url);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const load = () => api.get("/client/trips").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));

  useEffect(() => {
    load();
    if (user) setForm((f) => ({ ...f, customer_name: user.name, customer_email: user.email, customer_phone: user.phone || "" }));    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Keep the stage and any new messages fresh without a manual refresh.
  useEffect(() => {
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const { data: created } = await api.post("/client/bookings", {
        ...form,
        passengers: Number(form.passengers || 1),
        hours: Number(form.hours || 0),
        miles: null,
      });
      setBooked(created);
      setShowForm(false);
      // Pay now only if the operator allows it before they confirm the trip.
      if (Number(created.deposit_due || 0) > 0 && created.pay_before_approval !== false) {
        setMsg("");
        payDeposit(created.id);
      } else if (Number(created.deposit_due || 0) > 0) {
        setMsg(`Trip request sent. The office will confirm it, then ask you for ${money(created.deposit_due)}.`);
      } else {
        setMsg("Trip request sent. The dispatch team will confirm shortly.");
      }
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="client-portal" className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-2 px-4">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-slate-100">{company?.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Client portal</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button data-testid="client-account-link" size="sm" variant="outline" aria-label="Your account"
              onClick={() => navigate("/portal/client/account")}
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
              <UserCog className="h-4 w-4" />
            </Button>
            <Button data-testid="client-logout" variant="ghost" size="sm" aria-label="Sign out"
              className="px-2 text-slate-400 hover:text-gold sm:px-3"
              onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-4 pb-32 pt-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">Welcome, {user?.name}</h1>
            <p className="mt-2 text-sm text-slate-400">Your reservations and invoices with {company?.name}.</p>
          </div>
          <Button data-testid="client-book-btn" onClick={() => setShowForm(!showForm)} className="bg-gold text-ink-900 hover:bg-gold-light">
            <Plus className="mr-2 h-4 w-4" /> Book a trip
          </Button>
        </div>

        {msg && <p data-testid="client-success" className="text-sm text-emerald-400">{msg}</p>}
        {error && <p data-testid="client-error" className="text-sm text-red-400">{error}</p>}

        {booked && Number(booked.deposit_due || 0) > 0 && company?.online_payments_ready
          && booked.pay_before_approval !== false && (
          <div data-testid="client-payment-panel" className="rounded-xl border border-gold/40 bg-gold/5 p-6">
            <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Reserve your ride</p>
            <p className="mt-2 text-sm text-slate-300">
              Estimated total {money(booked.estimated_total)} · due now{" "}
              <span className="font-semibold text-slate-50">{money(booked.deposit_due)}</span>
            </p>
            <Button data-testid="client-pay-deposit" disabled={busy} onClick={payDeposit}
              className="mt-4 h-14 w-full bg-gold text-base text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${money(booked.deposit_due)} now`}
            </Button>
            {company?.payments_sandbox && (
              <div className="mt-3">
                <SandboxNotice testId="client-sandbox" />
              </div>
            )}
            {checkoutUrl && (
              <a data-testid="client-checkout-link" href={checkoutUrl} target="_blank" rel="noopener noreferrer"
                className="mt-3 block break-all text-center font-mono text-xs text-gold underline">
                Open the secure payment page
              </a>
            )}
            <button type="button" data-testid="client-pay-later" onClick={() => { setBooked(null); setMsg("Trip request sent. The dispatch team will confirm shortly."); }}
              className="mt-3 w-full font-mono text-[11px] uppercase tracking-widest text-slate-500">
              I'll pay later
            </button>
          </div>
        )}

        {showForm && (
          <form data-testid="client-booking-form" onSubmit={submit} className="space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6">
            <h2 className="text-lg font-medium text-slate-100">New trip request</h2>
            <AddressInput label="Pickup address" testId="client-pickup" companyId={cid} useMyLocation value={form.pickup}
              onChange={(p) => { setForm((f) => ({ ...f, pickup: p })); setQuote(null); }} />
            <AddressInput label="Drop-off address" testId="client-dropoff" companyId={cid} value={form.dropoff}
              onChange={(p) => { setForm((f) => ({ ...f, dropoff: p })); setQuote(null); }} />

            {showMap && (
              <RouteMap points={points} testId="client-map"
                onRoute={({ miles: m }) => { if (m != null) setMiles(m); }} />
            )}

            {vehicles.length > 0 && (
              <div>
                <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Vehicle</label>
                <div className="space-y-2">
                  {vehicles.map((v) => (
                    <button type="button" key={v.id} data-testid={`client-vehicle-${v.id}`}
                      onClick={() => { setForm((f) => ({ ...f, vehicle_id: v.id, pricing_mode: v.pricing_mode, vehicle_pref: v.name })); }}
                      className={`flex w-full items-center gap-4 rounded-xl border p-3 text-left transition-colors ${
                        form.vehicle_id === v.id ? "border-gold/50 bg-gold/10" : "border-ink-600 bg-ink-800 hover:border-gold/30"}`}>
                      <VehicleGallery photos={v.photos} name={v.name} testId={`client-gallery-${v.id}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm leading-tight text-slate-100">{v.name}</span>
                        <span className="block font-mono text-[11px] text-slate-500">
                          {v.capacity} pax · {v.luggage_capacity} bags
                          {v.pricing_mode === "hourly" ? ` · ${v.minimum_hours}h minimum` : ""}
                        </span>
                      </span>
                      <span data-testid={`client-vehicle-price-${v.id}`} className="shrink-0 text-right">
                        {prices[v.id]
                          ? <span className="font-display text-base text-gold">{money(prices[v.id].total)}</span>
                          : pricing
                            ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                            : <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">add route</span>}
                      </span>
                    </button>
                  ))}
                </div>
                {!form.vehicle_id && (
                  <p data-testid="client-quote-hint" className="mt-2 text-xs text-slate-500">
                    {Object.keys(prices).length > 0
                      ? "Prices are worked out on the driving distance for your route. Pick a vehicle to reserve it."
                      : "Enter your pickup and drop-off and every vehicle will show its price."}
                  </p>
                )}
                {quote && (
                  <div className="mt-4" data-testid="client-quote">
                    <QuoteBreakdown quote={quote} testId="client-quote-breakdown" label="Your price" simple />
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Pickup date</label>
                <Input data-testid="client-date" type="date" required value={form.trip_date} onChange={set("trip_date")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Pickup time</label>
                <Input data-testid="client-time" type="time" required value={form.pickup_time} onChange={set("pickup_time")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Passengers</label>
                <Input data-testid="client-passengers" type="number" min="1" value={form.passengers} onChange={set("passengers")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Airline (optional)</label>
                <Input data-testid="client-airline" placeholder="United" value={form.airline} onChange={set("airline")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Flight number (optional)</label>
                <Input data-testid="client-flight" placeholder="UA2201" value={form.flight_number} onChange={set("flight_number")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Vehicle preference</label>
                <Input data-testid="client-vehicle-pref" placeholder="Any" value={form.vehicle_pref} onChange={set("vehicle_pref")} className="h-11 border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
              </div>
            </div>
            <Textarea data-testid="client-notes" rows={3} placeholder="Anything the chauffeur should know" value={form.notes} onChange={set("notes")} className="border-ink-600 bg-ink-800 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold" />
            {company?.payment_policy?.auto_charge_balance && company?.online_payments_ready
              && (company?.payment_processor || "stripe") === "stripe" && (
              <label data-testid="client-save-card-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-ink-600 bg-ink-800 p-4">
                <input data-testid="client-save-card" type="checkbox" checked={form.save_card}
                  onChange={(e) => setForm((f) => ({ ...f, save_card: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-gold" />
                <span className="text-xs leading-relaxed text-slate-400">
                  Keep my card on file and settle the rest of my fare after the trip — the balance is
                  charged automatically about 30 minutes after we arrive. You can still add a
                  gratuity afterwards.
                </span>
              </label>
            )}
            <Button data-testid="client-booking-submit" type="submit" disabled={busy} className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
            </Button>
          </form>
        )}

        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your trips</p>
          <div className="mt-4 space-y-3">
            {data.trips.length === 0 && (
              <p data-testid="client-trips-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-500">
                No confirmed trips yet.
              </p>
            )}
            {data.trips.map((t) => (
              <div key={t.id} data-testid={`client-trip-${t.number}`} className="rounded-xl border border-ink-600 bg-ink-800 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-mono text-sm text-gold">TRIP #{t.number}</p>
                  <div className="flex items-center gap-2">
                    <span data-testid={`client-driver-status-${t.number}`} className="rounded-full border border-ink-600 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      <StageBadge stage={t.stage || "booked"} testId={`client-stage-${t.number}`} />
                    </span>
                    <StatusPill status={t.status} />
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-200">{t.pickup?.address} → {t.dropoff?.address}</p>
                <button type="button" data-testid={`client-ride-again-${t.number}`} onClick={() => rideAgain(t)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-ink-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/40 hover:text-gold">
                  <Repeat className="h-3 w-3" /> Ride again
                </button>
                {(t.stops || []).map((s, i) => (
                  <p key={s.place_id || `${s.address}-${i}`} className="mt-1 pl-3 text-xs text-slate-300">
                    Stop {i + 1}: {s.address}{" "}
                    <span className={`font-mono text-[10px] uppercase ${s.status === "pending" ? "text-amber-300" : s.status === "declined" ? "text-red-300" : "text-emerald-300"}`}>
                      {s.status}
                    </span>
                  </p>
                ))}
                <p className="mt-1 text-xs text-slate-300">
                  {t.trip_date} {t.pickup_time} · {t.vehicle_name || "vehicle TBD"} ·{" "}
                  <span className="text-slate-100">{t.final_total ? `final ${money(t.final_total)}` : `estimate ${money(t.total_charge)}`}</span>
                  {t.balance_due ? <span className="text-gold"> · balance {money(t.balance_due)}</span> : ""}
                </p>
                {!["completed", "declined"].includes(t.status) && (
                  <div className="mt-4 space-y-3 border-t border-ink-600 pt-4">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Button data-testid={`client-track-${t.number}`} variant="outline"
                        className="h-12 border-ink-600 bg-transparent text-slate-300 hover:text-gold"
                        onClick={async () => {
                          setError("");
                          try {
                            const { data: res } = await api.post(`/client/trips/${t.id}/track-link`);
                            navigate(`/portal/client/track/${res.token}`);
                          } catch (e) { setError(errMsg(e)); }
                        }}>
                        <Navigation className="mr-2 h-4 w-4" /> Track my ride
                      </Button>
                      <button data-testid={`client-messages-${t.number}`}
                        onClick={() => setChatFor(chatFor === t.id ? null : t.id)}
                        className="relative flex h-12 items-center justify-center gap-2 rounded-md border border-ink-600 text-sm font-medium text-slate-300 hover:text-gold">
                        <MessageSquare className="h-4 w-4" /> Message my chauffeur
                        {t.unread_messages > 0 && chatFor !== t.id && (
                          <span data-testid={`client-unread-${t.number}`}
                            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
                            {t.unread_messages}
                          </span>
                        )}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${chatFor === t.id ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {chatFor === t.id && (
                      <ChatPanel compact mine="passenger"
                        listUrl={`/client/trips/${t.id}/messages`}
                        sendUrl={`/client/trips/${t.id}/messages`}
                        title={`Message ${t.chauffeur_name || "your chauffeur"}`} />
                    )}
                    {dueNow(t) > 0 && company?.online_payments_ready && (
                      <Button data-testid={`client-pay-balance-${t.number}`} disabled={busy}
                        onClick={() => payTrip(t)}
                        className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
                        <CreditCard className="mr-2 h-4 w-4" />
                        {Number(t.deposit_due || 0) > Number(t.amount_paid || 0)
                          ? `Pay ${money(dueNow(t))} to confirm this trip`
                          : `Pay ${money(dueNow(t))} · add a tip`}
                      </Button>
                    )}
                    {stopFor === t.id ? (
                      <div className="space-y-3">
                        <AddressInput label="New stop address" testId={`client-stop-input-${t.number}`} companyId={cid} value={stopPlace}
                          onChange={setStopPlace} placeholder="Restaurant, office, hotel…" />
                        {t.stop_fee > 0 && (
                          <p data-testid={`client-stop-fee-${t.number}`} className="rounded-lg border border-gold/30 bg-gold/10 p-3 text-xs text-gold">
                            Adding a stop costs {money(t.stop_fee)}. You'll be taken straight to payment
                            once you confirm.
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button data-testid={`client-stop-submit-${t.number}`} disabled={busy} className="bg-gold text-ink-900 hover:bg-gold-light"
                            onClick={async () => {
                              setBusy(true); setError(""); setMsg("");
                              try {
                                const { data: res } = await api.post(`/client/trips/${t.id}/stops`, stopPlace);
                                setStopFor(null); setStopPlace(emptyPlace); load();
                                if (res.pay_token) {
                                  navigate(`/portal/client/pay/${res.pay_token}`);
                                  return;
                                }
                                setMsg(res.stop.status === "pending"
                                  ? res.charge > 0
                                    ? `Stop requested — dispatch will approve it shortly and ${money(res.charge)} will be added to your trip.`
                                    : "Stop requested — dispatch will approve it shortly."
                                  : res.charge > 0
                                    ? `Stop added — ${money(res.charge)} was added to your trip total.`
                                    : "Stop added to your itinerary.");
                              } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
                            }}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                              : t.stop_fee > 0 ? `Add stop · ${money(t.stop_fee)}` : "Request stop"}
                          </Button>
                          <Button variant="ghost" className="text-slate-400" onClick={() => setStopFor(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button data-testid={`client-add-stop-${t.number}`} variant="outline"
                        onClick={() => { setStopFor(t.id); setStopPlace(emptyPlace); }}
                        className="h-12 w-full border-ink-600 bg-transparent text-slate-300 hover:text-gold sm:w-auto">
                        <MapPin className="mr-2 h-4 w-4" /> + Add stop
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your invoices</p>
          <div className="mt-4 space-y-3">
            {data.invoices.length === 0 && (
              <p data-testid="client-invoices-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-6 text-sm text-slate-500">
                No invoices yet.
              </p>
            )}
            {data.invoices.map((i) => (
              <Link
                key={i.id}
                to={`/portal/invoice/${i.id}`}
                data-testid={`client-invoice-${i.number}`}
                className="flex items-center justify-between gap-4 rounded-xl border border-ink-600 bg-ink-800 p-5 transition-colors hover:border-gold/40"
              >
                <div>
                  <p className="font-mono text-sm text-gold">INVOICE #{i.number}</p>
                  <p className="mt-1 text-xs text-slate-500">Due {String(i.due_at).slice(0, 10)}</p>
                </div>
                <div className="text-right">
                  <p className="font-display text-lg text-slate-50">{money(i.amount)}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{i.status}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>



        <ContactCard company={company} testId="client-contact" />

        {company?.show_powered_by && (
          <p className="text-center font-mono text-[11px] text-slate-600">Powered by {PLATFORM_NAME}</p>
        )}
      </main>
      <Assistant />
      <HelpButton app="client" />
    </div>
  );
}
